/**
 * Sales Rep Dashboard — read-only view of own commissions.
 * Route: /sales/dashboard (role: sales_rep, admin, super_admin)
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, DollarSign, TrendingUp, Wallet, Clock, Briefcase, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import CrmManager from "@/components/admin/CrmManager";
import RepHospitalsView from "@/components/sales/RepHospitalsView";

const API = process.env.REACT_APP_BACKEND_URL;
const fmtMoney = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const STATUS_CHIP = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-blue-100 text-blue-800",
  paid: "bg-emerald-100 text-emerald-800",
  void: "bg-slate-100 text-slate-600",
};
const STATUS_LABEL = { pending: "Pending", approved: "Approved", paid: "Paid", void: "Void" };

const SalesRepDashboard = () => {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [me, setMe] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("crm"); // crm | commissions

  useEffect(() => {
    const load = async () => {
      try {
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/api/sales/me`, { headers }),
          axios.get(`${API}/api/sales/commissions`, { headers }),
        ]);
        setMe(r1.data);
        setCommissions(r2.data.commissions || []);
        setSummary(r2.data.summary);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    };
    if (token) load();
  }, [token, headers]);

  if (loading) return <div className="p-8 text-center text-slate-400">Loading dashboard...</div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sales-rep-dashboard">
      <header className="bg-emerald-700 text-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <DollarSign className="w-6 h-6" />
              {me?.name || user?.name || "Sales Rep"} Dashboard
            </h1>
            <p className="text-emerald-100 text-sm">{me?.role_label} · Default {me?.default_commission_pct}%</p>
          </div>
          <Button variant="outline" onClick={() => { logout(); navigate("/login"); }} className="bg-white text-emerald-700 hover:bg-emerald-50 gap-2" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Sign Out
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <div className="border-b border-slate-200 flex gap-1">
          <button
            onClick={() => setTab("crm")}
            className={`px-4 py-2 text-sm font-bold border-b-2 inline-flex items-center gap-2 ${tab === "crm" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid="rep-tab-crm"
          >
            <Briefcase className="w-4 h-4" /> My CRM (Customers · Leads · Activities)
          </button>
          <button
            onClick={() => setTab("hospitals")}
            className={`px-4 py-2 text-sm font-bold border-b-2 inline-flex items-center gap-2 ${tab === "hospitals" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid="rep-tab-hospitals"
          >
            <Building2 className="w-4 h-4" /> My Hospitals
          </button>
          <button
            onClick={() => setTab("commissions")}
            className={`px-4 py-2 text-sm font-bold border-b-2 inline-flex items-center gap-2 ${tab === "commissions" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid="rep-tab-commissions"
          >
            <DollarSign className="w-4 h-4" /> My Commissions
          </button>
        </div>

        {tab === "crm" && <CrmManager scope="rep" />}
        {tab === "hospitals" && <RepHospitalsView />}

        {tab === "commissions" && (
          <>
            {summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card icon={Clock} accent="amber" label="Pending" value={fmtMoney(summary.pending.total)} sub={`${summary.pending.count} lines awaiting approval`} />
                <Card icon={TrendingUp} accent="blue" label="Approved" value={fmtMoney(summary.approved.total)} sub={`${summary.approved.count} ready to pay`} />
                <Card icon={Wallet} accent="emerald" label="Paid YTD" value={fmtMoney(summary.paid.total)} sub={`${summary.paid.count} closed`} />
                <Card icon={DollarSign} accent="slate" label="Total Earned" value={fmtMoney(summary.pending.total + summary.approved.total + summary.paid.total)} sub="Across all statuses" />
              </div>
            )}

            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="px-4 py-3 border-b bg-slate-50">
                <h2 className="font-bold text-slate-800">My Commissions</h2>
                <p className="text-xs text-slate-500">Auto-generated when supplier orders are invoiced. Read-only — contact George for adjustments.</p>
              </div>
              {commissions.length === 0 ? (
                <p className="p-8 text-center text-slate-400 italic">No commissions yet.</p>
              ) : (
                <table className="w-full text-sm" data-testid="my-commissions-table">
                  <thead className="bg-slate-50 border-b text-xs uppercase tracking-wide text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left">PO</th>
                      <th className="px-3 py-2 text-left">Hospital</th>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-right">Gross Profit</th>
                      <th className="px-3 py-2 text-right">%</th>
                      <th className="px-3 py-2 text-right">Commission</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.map((c) => (
                      <tr key={c.id} className="border-b hover:bg-slate-50">
                        <td className="px-3 py-2 font-mono text-xs">{c.po_number || "—"}</td>
                        <td className="px-3 py-2 max-w-xs truncate">{c.hospital_name || "—"}</td>
                        <td className="px-3 py-2 max-w-md truncate text-slate-600">{c.description || "—"}</td>
                        <td className="px-3 py-2 text-right text-slate-700">{fmtMoney(c.gross_profit)}</td>
                        <td className="px-3 py-2 text-right">{c.commission_pct}%</td>
                        <td className="px-3 py-2 text-right font-bold text-emerald-700">{fmtMoney(c.commission_amount)}</td>
                        <td className="px-3 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${STATUS_CHIP[c.status]}`}>{STATUS_LABEL[c.status]}</span>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">{c.paid_at || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

const Card = ({ icon: Icon, accent, label, value, sub }) => {
  const cls = {
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  }[accent];
  return (
    <div className={`border rounded-xl p-4 ${cls}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide font-bold opacity-80">{label}</span>
        <Icon className="w-5 h-5 opacity-60" />
      </div>
      <div className="text-2xl font-bold mt-2">{value}</div>
      <div className="text-xs opacity-70 mt-1">{sub}</div>
    </div>
  );
};

export default SalesRepDashboard;
